/**
 * Accept Invitation Page
 * 
 * Handles invitation acceptance for both:
 * - Authenticated users (just accept invitation)
 * - New users (register and accept invitation)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { PageContainer } from '../components/UI/PageContainer';
import { PageHeader } from '../components/UI/PageHeader';
import { Button } from '../components/UI/Button';
import { Input } from '../components/UI/Input';
import { Card } from '../components/UI/Card';
import { useToastStore } from '../stores/toast';
import { useAuthContext } from '../components/Auth/AuthProvider';
import { acceptInvitation, acceptInvitationWithRegistration, getInvitationDetails, type AcceptInvitationWithRegistrationRequest } from '../api/teams';
import { Mail, Shield, CheckCircle, AlertCircle, User, Lock, UserPlus, Eye, EyeOff } from 'lucide-react';
import { getBaseUrl } from '../utils/env';
import { getAPIBaseUrl } from '../api/client';
import { ToastContainer } from '../components/UI/Toast';

export function AcceptInvitation() {
  // Get token from URL search params - handle both encoded and unencoded
  const getTokenFromUrl = () => {
    try {
      // First try standard URLSearchParams
      const params = new URLSearchParams(window.location.search);
      let token = params.get('token');
      
      // If not found, try decoding the entire search string (handles double-encoding)
      if (!token && window.location.search) {
        const decoded = decodeURIComponent(window.location.search);
        const decodedParams = new URLSearchParams(decoded);
        token = decodedParams.get('token');
      }
      
      // If still not found, try parsing from hash or path
      if (!token) {
        const hash = window.location.hash;
        if (hash.includes('token=')) {
          const hashParams = new URLSearchParams(hash.substring(1));
          token = hashParams.get('token');
        }
      }
      
      return token;
    } catch (error) {
      console.error('Error parsing token from URL:', error);
      return null;
    }
  };
  
  const [token] = useState(() => getTokenFromUrl());
  const [invitationDetails, setInvitationDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(() => !!token);
  /** Prevents applying results or toasts from stale/aborted fetches (Strict Mode, fast remounts). */
  const invitationFetchGeneration = useRef(0);

  // Get hooks - must be declared before useEffects that use them
  const { isAuthenticated, user } = useAuthContext();
  const { addToast } = useToastStore();
  
  // For unauthenticated users, always show registration form once details are loaded
  const [showRegistration, setShowRegistration] = useState(!isAuthenticated);
  
  // Navigation helper
  const navigate = (path: string) => {
    const baseUrl = getBaseUrl();
    const fullPath = baseUrl === '/' ? path : baseUrl.replace(/\/$/, '') + path;
    window.history.pushState({}, '', fullPath);
    // Trigger a popstate event to update the app's routing
    window.dispatchEvent(new PopStateEvent('popstate'));
  };
  
  // Fetch invitation details (token-only deps; use getState() for toasts to avoid effect churn from Zustand)
  useEffect(() => {
    if (!token) {
      setLoadingDetails(false);
      setInvitationDetails(null);
      return;
    }

    invitationFetchGeneration.current += 1;
    const generation = invitationFetchGeneration.current;
    const controller = new AbortController();

    setLoadingDetails(true);
    setInvitationDetails(null);

    const toast = useToastStore.getState().addToast;

    getInvitationDetails(token, { signal: controller.signal })
      .then((details) => {
        if (generation !== invitationFetchGeneration.current) return;
        setInvitationDetails(details);
        setLoadingDetails(false);
        if (!details.is_valid) {
          if (details.is_expired) {
            toast({
              type: 'error',
              message:
                'This invitation has expired. Please contact the person who invited you for a new invitation.',
            });
          } else if (details.status !== 'pending') {
            toast({
              type: 'error',
              message: 'This invitation has already been processed.',
            });
          }
        }
      })
      .catch((error: unknown) => {
        if (generation !== invitationFetchGeneration.current) return;
        const err = error as { code?: string; name?: string; message?: string };
        if (
          err?.code === 'ERR_CANCELED' ||
          err?.name === 'CanceledError' ||
          (typeof err?.message === 'string' && err.message.includes('canceled'))
        ) {
          return;
        }
        console.error('Failed to fetch invitation details:', error);
        setInvitationDetails(null);
        setLoadingDetails(false);
        toast({
          type: 'error',
          message: 'Failed to load invitation details. Please check your invitation link.',
        });
      });

    return () => {
      controller.abort();
    };
  }, [token]);
  
  const [formData, setFormData] = useState<AcceptInvitationWithRegistrationRequest>({
    token: token || '',
    username: '',
    password: '',
    full_name: '',
  });
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // If authenticated, try to accept directly
  const acceptMutation = useMutation({
    mutationFn: acceptInvitation,
    onSuccess: (data) => {
      addToast({
        type: 'success',
        message: data.message || 'Invitation accepted successfully!',
      });
      // Redirect to home or tenant/project
      if (data.tenant_id) {
        navigate(`/?tenant=${data.tenant_id}`);
      } else {
        navigate('/');
      }
      // Force page reload to update routing
      window.location.href = window.location.origin + (getBaseUrl() === '/' ? '/' : getBaseUrl()) + (data.tenant_id ? `?tenant=${data.tenant_id}` : '');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || 'Failed to accept invitation';
      // If error suggests registration or email mismatch, show registration form
      if (errorMessage.includes('create an account') || 
          errorMessage.includes('registration form') ||
          errorMessage.includes('email') || 
          errorMessage.includes('different email') ||
          errorMessage.includes('logged in as')) {
        // Show registration form
        setShowRegistration(true);
        addToast({
          type: 'info',
          message: 'Please create an account to accept this invitation',
        });
      } else {
        addToast({
          type: 'error',
          message: errorMessage,
        });
      }
    },
  });

  // Registration + acceptance mutation
  const registerAndAcceptMutation = useMutation({
    mutationFn: acceptInvitationWithRegistration,
    onSuccess: async (data) => {
      addToast({
        type: 'success',
        message: data.message || 'Account created and invitation accepted!',
      });
      
      // Auto-login the newly created user if tokens are provided
      if (data.access_token) {
        try {
          // Import auth store
          const { useAuthStore } = await import('../stores/auth');
          const authStore = useAuthStore.getState();
          
          // Set tokens and basic user info
          authStore.setAccessToken(
            data.access_token,
            data.refresh_token,
            data.user_id ? {
              id: data.user_id,
              email: invitationDetails?.email || '',
              username: formData.username,
              full_name: formData.full_name,
              tenant_id: data.tenant_id,
              is_email_verified: true, // Trust invitation email verification
            } : undefined
          );
          
          // Also set in API client
          const { getAPIClient } = await import('../api/client');
          const client = getAPIClient();
          client.setAccessToken(data.access_token);
          
          // Fetch user profile
          try {
            const userResponse = await client.get('/users/profile');
            if (userResponse.data) {
              authStore.setUser(userResponse.data);
            }
          } catch (profileError) {
            console.warn('Failed to fetch user profile after registration:', profileError);
            // Continue anyway - user can refresh
          }
          
          // Redirect to home or tenant/project
          const baseUrl = getBaseUrl();
          if (data.tenant_id) {
            window.location.href = window.location.origin + (baseUrl === '/' ? '/' : baseUrl) + `?tenant=${data.tenant_id}`;
          } else {
            window.location.href = window.location.origin + (baseUrl === '/' ? '/' : baseUrl);
          }
        } catch (error) {
          console.error('Auto-login failed:', error);
          // If auto-login fails, redirect to login
          const baseUrl = getBaseUrl();
          const loginPath = baseUrl === '/' ? '/login?registered=true' : baseUrl.replace(/\/$/, '') + '/login?registered=true';
          window.location.href = window.location.origin + loginPath;
        }
      } else {
        // No tokens provided, redirect to login
        const baseUrl = getBaseUrl();
        const loginPath = baseUrl === '/' ? '/login?registered=true' : baseUrl.replace(/\/$/, '') + '/login?registered=true';
        window.location.href = window.location.origin + loginPath;
      }
    },
    onError: (error: any) => {
      addToast({
        type: 'error',
        message: error?.response?.data?.detail || 'Failed to create account and accept invitation',
      });
    },
  });

  // Auto-accept if authenticated and token provided (only if email matches)
  useEffect(() => {
    // Only run after invitation details are loaded
    if (loadingDetails) {
      console.log('[AcceptInvitation] Still loading invitation details...');
      return;
    }
    
    if (!invitationDetails) {
      console.log('[AcceptInvitation] No invitation details available');
      return;
    }
    
    console.log('[AcceptInvitation] Invitation details loaded:', {
      isAuthenticated,
      hasToken: !!token,
      showRegistration,
      userEmail: user?.email,
      invitationEmail: invitationDetails.email,
      isValid: invitationDetails.is_valid
    });
    
    if (isAuthenticated && token && !showRegistration && user?.email && invitationDetails.is_valid) {
      // Check if email matches invitation email
      if (user.email.toLowerCase() === invitationDetails.email.toLowerCase()) {
        // Try to accept, but if email mismatch, show registration form
        console.log('[AcceptInvitation] Auto-accepting invitation for authenticated user');
        acceptMutation.mutate({ token });
      } else {
        // Email doesn't match, show registration form
        console.log('[AcceptInvitation] Email mismatch, showing registration form');
        setShowRegistration(true);
      }
    } else if (!isAuthenticated && token) {
      // For unauthenticated users, show registration form immediately
      console.log('[AcceptInvitation] Showing registration form for unauthenticated user');
      setShowRegistration(true);
    } else if (isAuthenticated && !user?.email) {
      // Authenticated but no email - show registration form
      console.log('[AcceptInvitation] Authenticated but no email, showing registration form');
      setShowRegistration(true);
    }
  }, [isAuthenticated, token, user?.email, loadingDetails, invitationDetails, showRegistration]);

  // Calculate password strength
  useEffect(() => {
    if (!formData.password) {
      setPasswordStrength(0);
      return;
    }

    let strength = 0;
    if (formData.password.length >= 8) strength++;
    if (/[A-Z]/.test(formData.password)) strength++;
    if (/[a-z]/.test(formData.password)) strength++;
    if (/[0-9]/.test(formData.password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) strength++;

    setPasswordStrength(strength);
  }, [formData.password]);

  const handleRegisterAndAccept = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.username || formData.username.length < 3) {
      addToast({ type: 'error', message: 'Username must be at least 3 characters' });
      return;
    }

    if (formData.password !== passwordConfirm) {
      addToast({ type: 'error', message: 'Passwords do not match' });
      return;
    }

    if (formData.password.length < 8) {
      addToast({ type: 'error', message: 'Password must be at least 8 characters' });
      return;
    }

    if (passwordStrength < 3) {
      addToast({ type: 'error', message: 'Password is too weak. Please include uppercase, lowercase, numbers, and special characters.' });
      return;
    }

    registerAndAcceptMutation.mutate(formData);
  };

  if (!token) {
    return (
      <><ToastContainer /><PageContainer>
        <Card className="max-w-md mx-auto mt-8">
          <div className="text-center p-8">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Invalid Invitation
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              No invitation token provided. Please check your invitation email.
            </p>
            <Button onClick={() => navigate('/login')}>Go to Login</Button>
          </div>
        </Card>
      </PageContainer></>
    );
  }

  // Show loading state while fetching invitation details
  if (loadingDetails) {
    return (
      <><ToastContainer /><PageContainer>
        <Card className="max-w-md mx-auto mt-8">
          <div className="text-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading invitation details...</p>
          </div>
        </Card>
      </PageContainer></>
    );
  }

  // Show expired invitation message
  if (invitationDetails && invitationDetails.is_expired) {
    return (
      <><ToastContainer /><PageContainer>
        <div className="max-w-md mx-auto mt-8">
          <PageHeader
            icon={<AlertCircle />}
            title="Invitation Expired"
            subtitle="This invitation link has expired"
          />
          
          <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
            <div className="p-6 space-y-4">
              <div className="flex items-start">
                <AlertCircle className="h-6 w-6 text-yellow-600 dark:text-yellow-400 mr-3 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    This invitation has expired
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                    The invitation link you clicked has expired. Invitations are valid for 7 days from the date they were sent.
                  </p>
                  
                  {invitationDetails && (
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-4 mb-4 space-y-2 text-sm">
                      <p className="text-gray-600 dark:text-gray-400">
                        <strong>Invited to:</strong> {invitationDetails.tenant_name || invitationDetails.project_name || 'the team'}
                      </p>
                      <p className="text-gray-600 dark:text-gray-400">
                        <strong>Email:</strong> {invitationDetails.email}
                      </p>
                      {invitationDetails.inviter_name && (
                        <p className="text-gray-600 dark:text-gray-400">
                          <strong>Invited by:</strong> {invitationDetails.inviter_name}
                        </p>
                      )}
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      What to do next:
                    </p>
                    <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 list-disc list-inside">
                      <li>Contact the person who invited you to request a new invitation</li>
                      <li>Or, if you already have an account, log in and check for pending invitations</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="flex-1"
                >
                  Go to Login
                </Button>
                {isAuthenticated && (
                  <Button
                    onClick={() => navigate('/')}
                    className="flex-1"
                  >
                    Go to Dashboard
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </div>
      </PageContainer></>
    );
  }

  // Show loading only if authenticated user is actively accepting
  if (isAuthenticated && token && !showRegistration && acceptMutation.isPending) {
    return (
      <><ToastContainer /><PageContainer>
        <Card className="max-w-md mx-auto mt-8">
          <div className="text-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Accepting invitation...</p>
          </div>
        </Card>
      </PageContainer></>
    );
  }

  // For unauthenticated users OR if registration form should be shown, display it
  // This is the default flow for new users - MUST be checked before any fallback
  if (showRegistration || (!isAuthenticated && token && !loadingDetails)) {
    return (
      <><ToastContainer /><PageContainer>
      <div className="max-w-md mx-auto mt-8">
        <PageHeader
          icon={<UserPlus className="h-8 w-8" />}
          title="Accept Invitation"
          subtitle={invitationDetails ? `You've been invited to join ${invitationDetails.tenant_name || invitationDetails.project_name || 'the team'}` : "Create your account to accept the invitation"}
        />
        
        {/* Display invitation details */}
        {invitationDetails && invitationDetails.is_valid && (
          <Card className="mb-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <div className="p-4 space-y-2">
              {invitationDetails.inviter_name && (
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Invited by:</strong> {invitationDetails.inviter_name}
                </p>
              )}
              {invitationDetails.role_name && (
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Role:</strong> {invitationDetails.role_name}
                </p>
              )}
              {invitationDetails.tenant_name && (
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Organization:</strong> {invitationDetails.tenant_name}
                </p>
              )}
              {invitationDetails.project_name && (
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Project:</strong> {invitationDetails.project_name}
                </p>
              )}
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <strong>Email:</strong> {invitationDetails.email}
              </p>
            </div>
          </Card>
        )}

        {/* Registration Form */}
        <Card>
          <form onSubmit={handleRegisterAndAccept} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address
              </label>
              <Input
                type="email"
                value={invitationDetails?.email || (loadingDetails ? 'Loading...' : '')}
                disabled
                className="w-full disabled:bg-gray-400 dark:bg-slate-800 text-white"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Email is pre-filled from your invitation
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Username <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="Choose a username"
                required
                className="w-full"
                leftIcon={<User className="h-5 w-5 text-gray-400" />}
                autoComplete="username"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                At least 3 characters, letters, numbers, underscores, and hyphens only
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Full Name (optional)
              </label>
              <Input
                type="text"
                value={formData.full_name || ''}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="Your full name"
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password <span className="text-red-500">*</span>
              </label>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Create a strong password"
                required
                className="w-full"
                leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                }
                autoComplete="new-password"
              />
              {formData.password && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded ${
                          level <= passwordStrength
                            ? passwordStrength <= 2
                              ? 'bg-red-500'
                              : passwordStrength <= 3
                              ? 'bg-yellow-500'
                              : 'bg-green-500'
                            : 'bg-gray-200 dark:bg-slate-700'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Password strength: {passwordStrength < 3 ? 'Weak' : passwordStrength < 4 ? 'Medium' : 'Strong'}
                  </p>
                </div>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                At least 8 characters with uppercase, lowercase, numbers, and special characters
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="Confirm your password"
                required
                className="w-full"
                leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                    title={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                }
              />
              {passwordConfirm && formData.password !== passwordConfirm && (
                <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
              )}
            </div>

            <div className="flex items-center gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/login')}
                disabled={registerAndAcceptMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  registerAndAcceptMutation.isPending ||
                  !formData.username ||
                  !formData.password ||
                  formData.password !== passwordConfirm ||
                  passwordStrength < 3
                }
                className="flex-1"
              >
                {registerAndAcceptMutation.isPending ? 'Creating Account...' : 'Create Account & Accept'}
              </Button>
            </div>
          </form>
        </Card>

        {/* OAuth Sign-in Options */}
        <div className="mt-6">
          {/* <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-slate-900 text-gray-500 dark:text-gray-400">Or continue with</span>
            </div>
          </div> */}
          
          {/* <div className="mt-4 grid grid-cols-2 gap-3"> */}
            {/* Microsoft/Azure AD SSO */}
            {/* <Button
              type="button"
              onClick={() => {
                const apiBaseUrl = getAPIBaseUrl();
                // Pass invitation token in state so we can accept it after OAuth
                const state = token ? `invitation:${token}` : undefined;
                const url = state 
                  ? `${apiBaseUrl}/auth/azure/login?state=${encodeURIComponent(state)}`
                  : `${apiBaseUrl}/auth/azure/login`;
                window.location.href = url;
              }}
              className="w-full h-11 text-sm font-medium bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 shadow-sm"
            >
              <svg className="h-5 w-5 mr-2" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 0H11V11H0V0Z" fill="#F25022"/>
                <path d="M12 0H23V11H12V0Z" fill="#7FBA00"/>
                <path d="M0 12H11V23H0V12Z" fill="#00A4EF"/>
                <path d="M12 12H23V23H12V12Z" fill="#FFB900"/>
              </svg>
              Microsoft
            </Button> */}
            
            {/* Google SSO */}
            {/* <Button
              type="button"
              onClick={() => {
                const apiBaseUrl = getAPIBaseUrl();
                // Pass invitation token in state so we can accept it after OAuth
                const state = token ? `invitation:${token}` : undefined;
                const url = state 
                  ? `${apiBaseUrl}/auth/google/login?state=${encodeURIComponent(state)}`
                  : `${apiBaseUrl}/auth/google/login`;
                window.location.href = url;
              }}
              className="w-full h-11 text-sm font-medium bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 shadow-sm"
            >
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Google
            </Button> */}
          {/* </div> */}
        </div>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              Log in here
            </button>
          </p>
        </div>
      </div>
    </PageContainer></>
    );
  }

  // Final fallback - should never reach here, but show loading just in case
  return (
    <><ToastContainer /><PageContainer>
      <Card className="max-w-md mx-auto mt-8">
        <div className="text-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </Card>
    </PageContainer></>
  );
}

